import React, { useEffect, useMemo } from 'react';
import cx from 'classnames';
import moment from 'moment';
import { Controller } from 'react-hook-form';
import { GrDocumentUpload } from 'react-icons/gr';
import { TiDelete } from 'react-icons/ti';
import * as yup from 'yup';
import StandardInput from '@@/components/Inputs/StandardInput';
import SelectPractitionerContactInput from '@@/components/Inputs/SelectPractitionerContactInput';
import DatePickerInput from '@@/components/Inputs/DatePickerInput';
import CheckboxInput from '@@/components/Inputs/CheckboxInput';
import MultiSelectInput from '@@/components/Inputs/MultiSelectInput';
import { formatContactAddress } from '@@/utils/address';
import { selectRecurringFrequencies } from '@@/constants/recurringEvent';

export const schema = yup
  .object({
    name: yup.string().required("Veuillez entrer le nom de l'évènement"),
    notes: yup.string(),
    location: yup.string(),
    targetPractitioner: yup.object({
      id: yup.string(),
      label: yup.string(),
    }),
    startDate: yup
      .string()
      .required("Veuillez entrer la date de début de l'évènement"),
    endDate: yup
      .string()
      .required("Veuillez entrer la date de fin de l'évènement"),
    travelTimeInMinutes: yup
      .number()
      .nullable(true)
      .transform((_, val?: number) => {
        if (val) {
          return Number(val);
        }
      }),
    shouldSendCalendarInvite: yup.boolean(),
    shouldSendScheduledMessage: yup.boolean(),
    scheduledMessageDate: yup.string(),
    scheduledMessageBody: yup.string(),
    recurringFrequency: yup.object({
      id: yup.string(),
      label: yup.string(),
    }),
  })
  .required();

const EventForm: React.FC<{
  form: any;
  birthDate?: string | Date;
  isDoctolib?: boolean;
  existingAttachments?: Unpatient.Document[];
  onDeleteDocument: (arg: string) => void;
}> = ({
  form,
  birthDate,
  isDoctolib,
  existingAttachments,
  onDeleteDocument,
}) => {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const [
    startDate,
    endDate,
    attachments,
    shouldSendScheduledMessage,
    targetPractitioner,
    recurringFrequency,
  ] = watch([
    'startDate',
    'endDate',
    'attachments',
    'shouldSendScheduledMessage',
    'targetPractitioner',
    'recurringFrequency',
  ]);

  useEffect(() => {
    if (typeof startDate?.getTime === 'function') {
      setValue('endDate', new Date(startDate.getTime() + 30 * 60 * 1000));
    }
  }, [startDate, setValue]);

  useEffect(() => {
    if (typeof endDate?.getTime === 'function' && shouldSendScheduledMessage) {
      setValue(
        'scheduledMessageDate',
        new Date(endDate.getTime() + 2 * 60 * 60 * 1000),
      );
    }
  }, [endDate, shouldSendScheduledMessage, setValue]);

  const birthdayWarning = useMemo(() => {
    if (!birthDate) {
      return false;
    }

    const mStartDate = moment(startDate);
    const mEndDate = moment(endDate);
    const mBirthDate = moment(birthDate).year(mStartDate.year());

    return mBirthDate.isBetween(mStartDate, mEndDate, null, '[]');
  }, [birthDate, startDate, endDate]);

  return (
    <div className="mt-8 mx-4">
      {birthdayWarning && (
        <p className="font-main text-orange-400">
          Attention, le patient a son anniversaire à cette date
        </p>
      )}

      <div key="event-form" className="grid grid-cols-3 gap-14">
        <StandardInput
          register={register}
          id="name"
          label="Nom de l'événement (*)"
          placeholder="Nom de l'événement"
          error={errors?.name}
          type="textarea"
          disabled={isDoctolib}
          rows={3}
        />

        <StandardInput
          register={register}
          id="location"
          label="Localisation de l'événement"
          placeholder="Localisation de l'événement"
          error={errors?.location}
          type="textarea"
          disabled={isDoctolib}
          rows={3}
        />
        <StandardInput
          register={register}
          id="notes"
          label="Notes"
          placeholder="Notes"
          error={errors?.notes}
          rows={3}
          type="textarea"
          disabled={isDoctolib}
        />

        <DatePickerInput
          id="startDate"
          control={control}
          label="Date de début de l'événement (*)"
          showTimeSelect
          error={errors?.startDate}
          disabled={isDoctolib}
        />
        <DatePickerInput
          id="endDate"
          control={control}
          label="Date de fin de l'événement (*)"
          showTimeSelect
          error={errors?.endDate}
          disabled={isDoctolib}
        />

        <StandardInput
          register={register}
          id="travelTimeInMinutes"
          label="Temps de trajet (en minutes)"
          placeholder="Temps de trajet (en minutes)"
          error={errors?.travelTimeInMinutes}
          type="text"
        />

        <div className="-mt-7">
          <SelectPractitionerContactInput
            control={control}
            id="targetPractitioner"
            values={targetPractitioner}
            label="Praticien à consulter"
            placeholder="Choisissez un praticien"
            cb={(contact: Unpatient.PractitionerContact) => {
              const location = formatContactAddress(contact);
              if (location) {
                setValue('location', location);
              }
            }}
            disabled={isDoctolib}
          />
        </div>

        <div className="-mt-7">
          <MultiSelectInput
            control={control}
            values={recurringFrequency}
            id="recurringFrequency"
            label="Récurrence"
            placeholder="Choisissez une fréquence"
            options={selectRecurringFrequencies}
            isMulti={false}
            isDisabled={isDoctolib}
          />
        </div>
      </div>

      <div className="my-4">
        <CheckboxInput
          register={register}
          id="shouldSendCalendarInvite"
          label="Envoyer une invitation au patient"
        />
      </div>

      <div className="my-4">
        <CheckboxInput
          register={register}
          id="shouldSendScheduledMessage"
          label="Ajouter un message programmé"
          disabled={isDoctolib}
        />

        {shouldSendScheduledMessage && (
          <div className="mt-10 w-[600px]">
            <StandardInput
              register={register}
              id="scheduledMessageBody"
              label="Message programmé (*)"
              placeholder="Message programmé (*)"
              error={errors?.scheduledMessageBody}
              type="textarea"
              rows={3}
            />

            <div className="mt-10">
              <DatePickerInput
                id="scheduledMessageDate"
                control={control}
                label="Date de d'envoi (*)"
                showTimeSelect
                error={errors?.scheduledMessageDate}
              />
            </div>
          </div>
        )}
      </div>

      <label
        htmlFor="attachments"
        className="mt-8 text-main font-main cursor-pointer flex flex-row items-center"
      >
        <GrDocumentUpload size={30} />
        <span className={cx('ml-2 underline')}>
          Ajouter une ou plusieurs pièces jointes
        </span>

        <Controller
          control={control}
          name="attachments"
          render={({ field }) => {
            return (
              <input
                {...field}
                value=""
                onChange={(event) => {
                  const newFiles = event.target.files
                    ? Array.from(event.target.files)
                    : [];
                  const existingFiles = Array.isArray(field.value)
                    ? field.value
                    : [];
                  field.onChange([...existingFiles, ...newFiles]);
                }}
                type="file"
                id="attachments"
                multiple
                className="hidden"
              />
            );
          }}
        />
      </label>

      {!!attachments?.length && (
        <div className="mt-2">
          {Array.from(attachments).map((file: any, i) => (
            <div key={i} className="flex flex-row items-center">
              <p className="font-main text-main">{file.name}</p>
              <button
                className="ml-4"
                onClick={() => {
                  const newFiles = Array.from(attachments).filter(
                    (_: any, index: number) => index !== i,
                  );
                  setValue('attachments', newFiles);
                }}
              >
                <TiDelete className="text-red-500" size={20} />
              </button>
            </div>
          ))}
        </div>
      )}

      {!!existingAttachments?.length && (
        <div className="mt-6">
          <p className="font-main text-main underline">
            Pièces jointes existantes :
          </p>
          {existingAttachments.map((attachment: Unpatient.Document, i) => (
            <div key={i} className="flex flex-row items-center mt-1">
              <p className="font-main text-main">{attachment.name}</p>
              <button
                className="ml-4"
                onClick={() => {
                  onDeleteDocument(attachment.id);
                }}
              >
                <TiDelete className="text-red-500" size={20} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventForm;
